'use client';

import { useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import { usePaymentConfig } from '@/tenants/_shared/hooks/usePaymentConfig';
import { useDonationCurrency } from '@/tenants/_shared/hooks/useCurrencies';
import {
  type DonationStrategy,
  type DonationSessionInput,
  type ActivePaymentSession,
} from './types';

export function useRandomDonationStrategy(eventId: string | undefined): DonationStrategy {
  const {
    data: paymentConfig,
    isLoading: isPaymentConfigLoading,
    isFetching: isPaymentConfigFetching,
    isError: isPaymentConfigError,
  } = usePaymentConfig(eventId);

  const currency = useDonationCurrency(eventId);

  const createRandomSession = trpc.publicDonation.createRandomEntrantSession.useMutation();

  return useMemo(
    (): DonationStrategy => ({
      paymentConfig,
      isPaymentConfigLoading,
      isPaymentConfigFetching,
      isPaymentConfigError,
      currency,
      isFundraisingActive: paymentConfig?.isFundraisingActive ?? true,
      isSessionCreationPending: createRandomSession.isPending,
      checkoutScope: { scope: 'random' as const, eventId: eventId! },
      createSession: (input: DonationSessionInput): Promise<ActivePaymentSession> =>
        createRandomSession.mutateAsync({ ...input, eventId: eventId! }),
    }),
    [
      paymentConfig,
      isPaymentConfigLoading,
      isPaymentConfigFetching,
      isPaymentConfigError,
      currency,
      createRandomSession,
      eventId,
    ],
  );
}
